import { useState } from "react";
import { useI18n } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Wand2, SpellCheck, ArrowRightToLine, Lightbulb, Palette, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ACTIONS = [
  { id: "expand", icon: Sparkles, label: "ai.expand" },
  { id: "rewrite", icon: Wand2, label: "ai.rewrite" },
  { id: "correct", icon: SpellCheck, label: "ai.correct" },
  { id: "continue", icon: ArrowRightToLine, label: "ai.continue" },
  { id: "tone", icon: Palette, label: "ai.tone" },
  { id: "idea", icon: Lightbulb, label: "ai.idea" },
];

export default function AIToolbar({ getSelection, applyResult, language, context }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(null);
  const [ideas, setIdeas] = useState(null);
  const [toneOpen, setToneOpen] = useState(false);
  const [toneText, setToneText] = useState("");

  const run = async (action, instruction = "") => {
    const sel = getSelection();
    const text = sel.text || sel.full;
    if (action !== "idea" && !text.trim()) {
      toast.error(t("ai.selectionHint"));
      return;
    }
    setBusy(action);
    try {
      const { data } = await api.post("/ai/assist", {
        action, text, instruction, language, context: action === "idea" ? sel.full : context,
      });
      if (action === "idea") {
        setIdeas(data.result);
      } else {
        applyResult(data.result, sel, action);
        toast.success(t("toast.aiDone"));
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const handleClick = (id) => {
    if (id === "tone") { setToneOpen(true); return; }
    run(id);
  };

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold pr-1">
          <Sparkles className="w-3.5 h-3.5" /> {t("ai.title")}
        </span>
        {ACTIONS.map((a) => {
          const Icon = a.icon;
          const isBusy = busy === a.id;
          return (
            <button
              key={a.id}
              disabled={!!busy}
              onClick={() => handleClick(a.id)}
              data-testid={`ai-action-${a.id === "correct" ? "correct" : a.id === "expand" ? "expand" : a.id === "rewrite" ? "rewrite" : a.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-ink-800 px-3 py-1.5 text-xs text-slate-300 hover:border-gold/40 hover:text-gold transition-colors disabled:opacity-50"
            >
              {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
              {t(a.label)}
            </button>
          );
        })}
      </div>

      {busy && (
        <div className="mt-2 text-xs text-gold/80 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-gold pulse-gold" /> {t("ai.working")}
        </div>
      )}

      {/* Tone dialog */}
      <Dialog open={toneOpen} onOpenChange={setToneOpen}>
        <DialogContent className="bg-ink-800 border-white/10 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl">{t("ai.tone")}</DialogTitle></DialogHeader>
          <Input
            autoFocus
            value={toneText}
            onChange={(e) => setToneText(e.target.value)}
            placeholder={t("ai.toneInstruction")}
            className="bg-ink-900 border-white/10 focus-visible:ring-gold"
            data-testid="ai-tone-input"
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setToneOpen(false)} className="text-slate-400">{t("ai.close")}</Button>
            <Button
              disabled={busy === "tone"}
              onClick={() => { setToneOpen(false); run("tone", toneText); }}
              className="bg-gold hover:bg-gold-light text-ink-900 font-semibold"
            >
              {busy === "tone" ? <Loader2 className="w-4 h-4 animate-spin" /> : t("ai.tone")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ideas dialog */}
      <Dialog open={!!ideas} onOpenChange={() => setIdeas(null)}>
        <DialogContent className="bg-ink-800 border-white/10 text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-xl flex items-center gap-2"><Lightbulb className="w-5 h-5 text-gold" /> {t("ai.ideasTitle")}</DialogTitle></DialogHeader>
          <div className="whitespace-pre-wrap text-sm text-slate-300 leading-relaxed max-h-[50vh] overflow-y-auto" data-testid="ai-ideas-content">
            {ideas}
          </div>
          <div className="flex justify-end">
            <Button variant="ghost" onClick={() => setIdeas(null)} className="text-slate-400">{t("ai.close")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
